import moment from "moment";

/** Formats a YYYY-MM-DD string as a long localized date for confirm modals. */
export const formatApplicationDateForModal = (ymd) => {
  if (ymd == null || ymd === "") return "—";
  const m = moment(ymd, "YYYY-MM-DD", true);
  return m.isValid() ? m.locale("es").format("LL") : String(ymd);
};

/** AAAA-MM-DD; útil con `RhfDateYmdField` o payloads API. */
export const toYMD = (value) => {
  if (value == null || value === "") return "";
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

export const todayYMD = () => moment().format("YYYY-MM-DD");

export const servScaleFormKeys = [
  "priceHitch",
  "priceKm",
  "nocturnalSurchargePct",
  "minPriceDayInt",
  "minPriceDayExt",
  "minPriceNightInt",
  "minPriceNightExt",
  "intTowPricePct",
  "extTowPricePct",
  "extTowEarnPct",
  "intTowEarnPct",
  "avEarnPct",
  "dateSince",
];

/** Campos editables del baremo (excluye `dateSince`, que no entra al historial por variable). */
export const servScaleHistoryFieldKeys = servScaleFormKeys.filter(
  (k) => k !== "dateSince"
);

/** Etiquetas para tabla de historial y filtros (alineadas al formulario). */
export const SERV_SCALE_FIELD_LABELS = {
  priceHitch: "Precio de enganche",
  priceKm: "Monto por kilómetro",
  nocturnalSurchargePct: "% Recargo nocturno",
  minPriceDayInt: "Monto mín. diurno — interno",
  minPriceDayExt: "Monto mín. diurno — externo",
  minPriceNightInt: "Monto mín. nocturno — interno",
  minPriceNightExt: "Monto mín. nocturno — externo",
  intTowPricePct: "% del monto — gruero interno",
  extTowPricePct: "% del monto — gruero externo",
  extTowEarnPct: "Gruero externo",
  intTowEarnPct: "Gruero interno",
  avEarnPct: "Asistente vial",
};

export const servScaleDefaultValues = () => ({
  priceHitch: 0,
  priceKm: 0,
  nocturnalSurchargePct: 0,
  minPriceDayInt: 0,
  minPriceDayExt: 0,
  minPriceNightInt: 0,
  minPriceNightExt: 0,
  intTowPricePct: 0,
  extTowPricePct: 0,
  extTowEarnPct: 0,
  intTowEarnPct: 0,
  avEarnPct: 0,
  dateSince: todayYMD(),
});

export const baremo = {
  field: "baremo-section__field",
  fieldDate: "baremo-section__field baremo-section__field--date",
  label: "baremo-section__label",
  inputWrap: "baremo-section__input-wrap",
  input: "baremo-section__input",
  inputDate: "baremo-section__input baremo-section__input--date",
  suffix: "baremo-section__suffix",
  fieldDescription: "baremo-section__field-description",
  error: "baremo-section__error",
};

export const MIN_S = 0;
export const MAX_MIN = 999;

export const PAGE_SIZE = 10;

const PCT_FIELDS = new Set([
  "nocturnalSurchargePct",
  "intTowPricePct",
  "extTowPricePct",
  "extTowEarnPct",
  "intTowEarnPct",
  "avEarnPct",
]);

const AVATAR_BG = ["#ff7020", "#c2410c", "#ea580c", "#9a3412", "#f97316"];

export function formatHistoryValue(field, value) {
  if (value === "" || value == null) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (PCT_FIELDS.has(field)) return `${n}%`;
  if (field === "priceKm") return `$${n.toFixed(2)}/km`;
  return `$${n.toFixed(2)}`;
}

export function userInitials(firstName, lastName) {
  const a = (firstName || "").trim();
  const b = (lastName || "").trim();
  if (a && b) return (a[0] + b[0]).toUpperCase();
  const full = [a, b].filter(Boolean).join(" ");
  if (!full) return "?";
  const p = full.split(/\s+/).filter(Boolean);
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[1][0]).toUpperCase();
}

export function userDisplayName(createdBy) {
  if (createdBy == null) return "—";
  if (typeof createdBy === "string") return createdBy.trim() || "—";
  const n =
    [createdBy.firstName, createdBy.lastName].filter(Boolean).join(" ").trim() ||
    createdBy.name ||
    createdBy.fullName ||
    createdBy.email ||
    "—";
  return n;
}

export function avatarBgForName(name) {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1)
    h = (h + s.charCodeAt(i)) % AVATAR_BG.length;
  return AVATAR_BG[h];
}

// ─── Operations Commission ────────────────────────────────────────────────────

export const operationsCommissionFormKeys = [
  "battery",
  "roleCoord",
  "roleOperator",
  "roleOperatorExtraSince",
  "roleOperatorExtraAmount",
  "roleSuperv",
  "solvedByAv",
  "dateSince",
];

export const operationsCommissionHistoryFieldKeys =
  operationsCommissionFormKeys.filter((k) => k !== "dateSince");

export const OPERATIONS_COMMISSION_FIELD_LABELS = {
  battery: "Venta de batería",
  roleCoord: "Coordinador",
  roleOperator: "Operador",
  roleOperatorExtraSince: "Extra operador (umbral)",
  roleOperatorExtraAmount: "Extra operador (monto)",
  roleSuperv: "Supervisor",
  solvedByAv: "Avería resuelta",
};

export const operationsCommissionDefaultValues = () => ({
  battery: 0,
  roleCoord: 0,
  roleOperator: 0,
  roleOperatorExtraSince: 0,
  roleOperatorExtraAmount: 0,
  roleSuperv: 0,
  solvedByAv: 0,
  dateSince: todayYMD(),
});

export function formatCommissionHistoryValue(field, value) {
  if (value === "" || value == null) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `$${n.toFixed(2)}`;
}
